<template>
  <div class="px-6 py-6 d-flex flex-column row-gap-4" data-locator="rights-content">
    <div>
      <p class="heading-xs-bold text-text-primary">{{ $t(`${TRANSLATION_KEY_PREFIX}.title`) }}</p>
      <p class="text-text-secondary">{{ $t(`${TRANSLATION_KEY_PREFIX}.text`) }}</p>
    </div>
    <app-tile-group
      :model-value="activeShareTypeOption"
      :options="shareTypeOptions"
      :disabled="areTaskRightsPending"
      @update:model-value="onChangeActiveOption"
      v-if="areTaskRightsLoaded"
    />
    <template v-if="activeShareTypeOption === SHARE_TYPE_OPTIONS.CUSTOM">
      <div class="d-flex flex-column row-gap-4 mt-2">
        <div>
          <p class="heading-xs-bold text-text-primary">{{ $t(`${TRANSLATION_KEY_PREFIX}.searchMembersLabel`) }}</p>
          <p class="text-text-secondary">{{ $t(`${TRANSLATION_KEY_PREFIX}.defaultObserversAlert`) }}</p>
        </div>
        <MembersAutocomplete
          :items="allRoomMembers"
          :placeholder="$t(`${TRANSLATION_KEY_PREFIX}.searchMembersPlaceholder`)"
          @select="onMemberSelect"
        />
        <DocumentSharePicker
          force-pm-selection
          reset-groups-and-members-in-parent
          v-if="areTaskRightsLoaded"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'

import DocumentSharePicker from '@/common/document-share/DocumentSharePicker.vue'
import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import { toast } from '@/common/utils/toast'
import MembersAutocomplete from '@/project/members/MembersAutocomplete.vue'
import { UPDATE_TASK_RIGHTS } from '@/store/modules/checklist/action_types'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { DocumentSharePickerMutationTypes } from '@/store/modules/document-share-picker/mutation_types'

const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistPaneRights'
const UPDATE_TASK_RIGHTS_DEBOUNCE_TIME = 500

export default {
  name: 'ChecklistPaneRights',
  components: { DocumentSharePicker, MembersAutocomplete },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapGetters('groups', ['allRoomMembers']),
    ...mapState('checklist', ['checklistPane', 'taskRightsPendingIds']),
    ...mapState('documentSharePicker', ['disabledMembers','selectedGroups','selectedMembers']),
    areTaskRightsLoaded () {
      return this.currentTask?.rights && this.activeTaskId === this.currentTask.id
    },
    areTaskRightsPending () {
      return this.taskRightsPendingIds.includes(this.currentTask?.id)
    },
    currentTask() {
      return this.checklistPane.tasks[0]
    },
    defaultObserversGroups() {
      return [...this.currentTask.rights.responsibles.groups, ...this.currentTask.rights.validators.groups]
    },
    defaultObserversUsers() {
      return [...this.currentTask.rights.responsibles.users, ...this.currentTask.rights.validators.users]
    },
    isActiveTab() {
      return this.checklistPane.tab === 'rights'
    },
    shareTypeOptions () {
      const shareTypeOptions = Object.values(SHARE_TYPE_OPTIONS).filter(option => option !== SHARE_TYPE_OPTIONS.PRIVATE)
      return shareTypeOptions.map(option => {
        return {
          label: this.$t(`${TRANSLATION_KEY_PREFIX}.shareTypeOptionsLabels.${option}`),
          value: option,
          dataLocator: `${option}-share-option`,
        }
      })
    },
  },
  data: () => ({
    SHARE_TYPE_OPTIONS,
    TRANSLATION_KEY_PREFIX,
    activeShareTypeOption: null,
    activeTaskId: null,
  }),
  watch: {
    isActiveTab: {
      immediate: true,
      handler (val) {
        if(val) {
          this.initTaskRights()
        } else {
          this.resetTaskRights()
        }
      },
    },
    currentTask: {
      immediate: true,
      handler (val) {
        if(val) {
          this.initTaskRights()
        }
      },
    },
    selectedMembers: {
      handler () {
        if(this.areTaskRightsLoaded && this.isActiveTab) {
          this.updateTaskRights()
        }
      },
    },
  },
  created () {
    this.updateTaskRights = debounce(this.updateTaskRights, UPDATE_TASK_RIGHTS_DEBOUNCE_TIME, { leading: true })
  },
  beforeUnmount () {
    this.resetTaskRights()
  },
  methods: {
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.DISABLE_GROUPS,
      DocumentSharePickerActionTypes.DISABLE_MEMBERS,
      DocumentSharePickerActionTypes.HIGHLIGHT_MEMBER,
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
      DocumentSharePickerActionTypes.RESET_DISABLED_GROUPS_AND_MEMBERS,
    ]),
    ...mapMutations('documentSharePicker', [
      DocumentSharePickerMutationTypes.ADD_MEMBERS_TO_SELECTED,
    ]),
    ...mapActions('checklist', [UPDATE_TASK_RIGHTS]),
    disableObservers() {
      this.DISABLE_GROUPS(this.defaultObserversGroups)
      this.DISABLE_MEMBERS(this.defaultObserversUsers)
    },
    async initTaskRights() {
      try {
        await this.INIT_SELECTED_GROUPS(this.currentTask.rights.observers?.groups || [])
        await this.INIT_SELECTED_MEMBERS(this.currentTask.rights.observers?.users || [])

        this.disableObservers()

        this.activeShareTypeOption = this.currentTask.rights.shareType
        this.activeTaskId = this.currentTask.id
      } catch (e) {
        console.error(e)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.initTaskRightsError`))
      }
    },
    async onChangeActiveOption (option) {
      this.activeShareTypeOption = option

      if(this.activeShareTypeOption !== this.currentTask.rights.shareType) {
        await this.updateTaskRights()
        this.disableObservers()
      }
    },
    onMemberSelect (member) {
      if (member) {
        this.ADD_MEMBERS_TO_SELECTED([member])
        this.HIGHLIGHT_MEMBER(member)
      }
    },
    resetTaskRights() {
      this.activeShareTypeOption = null
      this.activeTaskId = null
      this.RESET_DISABLED_GROUPS_AND_MEMBERS()
    },
    async updateTaskRights () {
      const data = {
        shareType: this.activeShareTypeOption,
        observers: {
          groups: this.selectedGroups.map(item => item.id),
          users: this.selectedMembers.map(item => item.id),
        },
      }

      try {
        await this.UPDATE_TASK_RIGHTS({
          data,
          mnemo: this.roomMnemo,
          task: this.currentTask,
        })

      } catch (e) {
        console.error(e)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.updateTaskRightsError`))
      }
    },
  },
};
</script>

<style scoped lang="scss">
:deep(.v-input--horizontal .v-input__prepend) {
  margin-inline-end: 0;
}
:deep(.v-text-field .v-input__details) {
  display: none;
}
</style>
